/**
 * Tool Guide content by role.
 * Independent of business logic — descriptions only.
 * Path style mirrors Windows Explorer: Root / Folder / Action
 */

export const TOOL_GUIDE_ROOT = "My Requests";

export const BUCKET_KEYS = {
  OUTSTANDING: "Project Requests Outstanding",
  RESPONSE_RECEIVED: "Response Received",
  PROJECT_APPROVED: "Project Approved",
  PROJECT_ASSIGNED: "Project Assigned",
  PROJECT_ONGOING: "Project Ongoing",
  PROJECT_COMPLETED: "Project Completed",
};

/** Hover key from SalesProjects → bucket display name */
export const HOVER_KEY_TO_BUCKET = {
  outstanding: BUCKET_KEYS.OUTSTANDING,
  responseReceived: BUCKET_KEYS.RESPONSE_RECEIVED,
  projectApproved: BUCKET_KEYS.PROJECT_APPROVED,
  projectAssigned: BUCKET_KEYS.PROJECT_ASSIGNED,
  projectOngoing: BUCKET_KEYS.PROJECT_ONGOING,
  projectCompleted: BUCKET_KEYS.PROJECT_COMPLETED,
};

/** Shared short steps reused across buckets */
const step = (id, label, detail, pathTrail) => ({
  id,
  label,
  pathLabel: label,
  detail,
  pathTrail: pathTrail || [label],
});

/**
 * Sales person (L3) — all My Requests buckets
 */
export const salesOutstandingGuide = {
  id: "project-requests-outstanding",
  title: "Project Requests Outstanding",
  shortTitle: "Outstanding",
  currentAction:
    "Select Project → Assign Task to Managers → Forward / Cancel / Reassign",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.OUTSTANDING],
  steps: [
    step("select-project", "Select Project", "Select the project name from the list."),
    step(
      "assign-managers",
      "Assign Task to Managers",
      "Assign managers (primary manager optional)."
    ),
    step("forward", "Forward", "Forward to all selected managers.", [
      "Assign Task to Managers",
      "Forward",
    ]),
    step("cancel", "Cancel", "Cancel the request if required.", [
      "Assign Task to Managers",
      "Cancel",
    ]),
    step("reassign", "Reassign", "Reassign to managers if needed.", [
      "Assign Task to Managers",
      "Reassign",
    ]),
  ],
};

export const salesResponseReceivedGuide = {
  id: "response-received",
  title: "Response Received",
  shortTitle: "Response Received",
  currentAction: "Project Name → View Details → Accept / Reject / Cancel",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.RESPONSE_RECEIVED],
  steps: [
    step("select-project", "Select Project", "Select the project from the list."),
    step("view-details", "View Details", "Open details to review manager response.", [
      "View Details",
    ]),
    step("accept", "Accept", "Accept the manager response.", [
      "View Details",
      "Accept",
    ]),
    step("reject", "Reject", "Reject and send back to Outstanding.", [
      "View Details",
      "Reject",
    ]),
    step("cancel", "Cancel", "Cancel the manager from this project.", [
      "View Details",
      "Cancel",
    ]),
  ],
};

export const salesProjectApprovedGuide = {
  id: "project-approved",
  title: "Project Approved",
  shortTitle: "Project Approved",
  currentAction:
    "Project Name → View Details → Select Resources → Accept / Forward / Cancel / Reassign",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.PROJECT_APPROVED],
  steps: [
    step("select-project", "Project Name", "Select the approved project."),
    step("view-details", "View Details", "Review approved project details.", [
      "View Details",
    ]),
    step(
      "select-resources",
      "Select Resources",
      "Select resources from the Assign To Managers dropdown.",
      ["Select Resources"]
    ),
    step(
      "assign-managers",
      "Assign Task to Managers",
      "Reassign to same manager or assign to new managers.",
      ["Select Resources"]
    ),
    step("accept", "Accept", "Accept and keep current assignment.", [
      "Select Resources",
      "Accept",
    ]),
    step("forward", "Forward", "Forward to selected managers.", [
      "Select Resources",
      "Forward",
    ]),
    step("cancel", "Cancel", "Cancel the project if required.", [
      "Cancel",
    ]),
    step("reassign", "Reassign", "Reassign managers after reject.", [
      "Select Resources",
      "Reassign",
    ]),
  ],
};

export const salesProjectAssignedGuide = {
  id: "project-assigned",
  title: "Project Assigned",
  shortTitle: "Project Assigned",
  currentAction: "Project Name → View Details → Accept / Reject Project",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.PROJECT_ASSIGNED],
  steps: [
    step("select-project", "Select Project", "Select the assigned project."),
    step("view-details", "View Details", "Review assigned project details.", [
      "View Details",
    ]),
    step("accept", "Accept", "Accept the project assignment.", [
      "View Details",
      "Accept",
    ]),
    step("reject", "Reject", "Reject the project assignment.", [
      "View Details",
      "Reject",
    ]),
  ],
};

export const salesProjectOngoingGuide = {
  id: "project-ongoing",
  title: "Project Ongoing",
  shortTitle: "Project Ongoing",
  currentAction: "Project Name → View Details → Approved Final Complete",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.PROJECT_ONGOING],
  steps: [
    step("select-project", "Select Project", "Select the ongoing project."),
    step("view-details", "View Details", "Review ongoing project progress.", [
      "View Details",
    ]),
    step(
      "approved-complete",
      "Approved Final Complete",
      "Approve final completion when ready.",
      ["View Details", "Approved Final Complete"]
    ),
  ],
};

export const salesProjectCompletedGuide = {
  id: "project-completed",
  title: "Project Completed",
  shortTitle: "Project Completed",
  currentAction: "Project Name → View Details",
  pathBase: [TOOL_GUIDE_ROOT, BUCKET_KEYS.PROJECT_COMPLETED],
  steps: [
    step("select-project", "Select Project", "Select the completed project."),
    step("view-details", "View Details", "View completed project details.", [
      "View Details",
    ]),
  ],
};

/** Manager UI bucket label (Operations Manager My Requests) */
export const MANAGER_BUCKET_KEYS = {
  PROJECT_REQUESTS: "Project Requests",
  PROJECT_APPROVED: "Project Approved",
  PROJECT_ASSIGNED: "Project Assigned",
  PROJECT_ONGOING: "Project Ongoing",
  PROJECT_COMPLETED: "Project Completed",
};

/**
 * Operations Manager (L4 project manager) — My Requests buckets
 */
export const managerProjectRequestsGuide = {
  id: "manager-project-requests",
  title: "Project Requests",
  shortTitle: "Project Requests",
  currentAction:
    "Project Name → Forward / Reject → Assign Tasks → Fill Details → Complete",
  pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_REQUESTS],
  steps: [
    step("select-project", "Project Name", "Select the project from the list."),
    step("forward", "Forward / Reject", "Forward the project request.", [
      "Forward / Reject",
    ]),
    step("reject", "Forward / Reject", "Reject the project request.", [
      "Forward / Reject",
    ]),
    step(
      "assign-employees",
      "Assign Tasks",
      "Assign tasks to employees and self.",
      ["Assign Tasks"]
    ),
    step("fill-details", "Fill Details", "Fill the task details.", [
      "Fill Details",
    ]),
    step("complete", "Complete", "Complete self/other submitted response.", [
      "Complete",
    ]),
  ],
};

export const managerProjectApprovedGuide = {
  id: "manager-project-approved",
  title: "Project Approved",
  shortTitle: "Project Approved",
  currentAction: "Project Name → View Details",
  pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_APPROVED],
  steps: [
    step("select-project", "Project Name", "Select the approved project."),
    step("view-details", "View Details", "Review approved project details.", [
      "View Details",
    ]),
  ],
};

export const managerProjectAssignedGuide = {
  id: "manager-project-assigned",
  title: "Project Assigned",
  shortTitle: "Project Assigned",
  currentAction:
    "Project Name → View Details → Assign task to Employees → Submit",
  pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED],
  steps: [
    step("select-project", "Project Name", "Select the assigned project."),
    step("view-details", "View Details", "Review assigned project details.", [
      "View Details",
    ]),
    step(
      "assign-employees",
      "Assign task to Employees",
      "Assign tasks to employees.",
      ["View Details", "Assign task to Employees"]
    ),
    step("submit-task", "Submit", "Submit the task for self and others.", [
      "View Details",
      "Submit",
    ]),
  ],
};

export const managerProjectOngoingGuide = {
  id: "manager-project-ongoing",
  title: "Project Ongoing",
  shortTitle: "Project Ongoing",
  currentAction: "Project Name → View Details → Submit Project",
  pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_ONGOING],
  steps: [
    step("select-project", "Select Project", "Select the ongoing project."),
    step("view-details", "View Details", "Review ongoing project progress.", [
      "View Details",
    ]),
    step("submit", "Submit Project", "Submit the project.", [
      "View Details",
      "Submit Project",
    ]),
  ],
};

export const managerProjectCompletedGuide = {
  id: "manager-project-completed",
  title: "Project Completed",
  shortTitle: "Project Completed",
  currentAction: "Project Name → View Details",
  pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_COMPLETED],
  steps: [
    step("select-project", "Project Name", "Select the completed project."),
    step("view-details", "View Details", "View completed project details.", [
      "View Details",
    ]),
  ],
};

/** Role → bucket → guide map */
export const toolGuideByRole = {
  L3: {
    [BUCKET_KEYS.OUTSTANDING]: salesOutstandingGuide,
    [BUCKET_KEYS.RESPONSE_RECEIVED]: salesResponseReceivedGuide,
    [BUCKET_KEYS.PROJECT_APPROVED]: salesProjectApprovedGuide,
    [BUCKET_KEYS.PROJECT_ASSIGNED]: salesProjectAssignedGuide,
    [BUCKET_KEYS.PROJECT_ONGOING]: salesProjectOngoingGuide,
    [BUCKET_KEYS.PROJECT_COMPLETED]: salesProjectCompletedGuide,
  },
  L4_PM: {
    [MANAGER_BUCKET_KEYS.PROJECT_REQUESTS]: managerProjectRequestsGuide,
    [MANAGER_BUCKET_KEYS.PROJECT_APPROVED]: managerProjectApprovedGuide,
    [MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED]: managerProjectAssignedGuide,
    [MANAGER_BUCKET_KEYS.PROJECT_ONGOING]: managerProjectOngoingGuide,
    [MANAGER_BUCKET_KEYS.PROJECT_COMPLETED]: managerProjectCompletedGuide,
  },
  L4_OPS: {
    [MANAGER_BUCKET_KEYS.PROJECT_REQUESTS]: {
      id: "ops-project-requests",
      title: "Project Requests",
      shortTitle: "Project Requests",
      currentAction:
        "Project Name → Accept / Reject → Fill the Task Details → Submit the Project",
      pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_REQUESTS],
      steps: [
        step("select-project", "Project Name", "Select the project from the list."),
        step("accept", "Accept / Reject", "Accept the project request.", [
          "Accept / Reject",
        ]),
        step("reject", "Accept / Reject", "Reject the project request.", [
          "Accept / Reject",
        ]),
        step("fill-details", "Fill the Task Details", "Fill the task details.", [
          "Fill the Task Details",
        ]),
        step("submit", "Submit the Project", "Submit the project.", [
          "Submit the Project",
        ]),
      ],
    },
    [MANAGER_BUCKET_KEYS.PROJECT_APPROVED]: {
      id: "ops-project-approved",
      title: "Project Approved",
      shortTitle: "Project Approved",
      currentAction: "Project Name → View Details",
      pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_APPROVED],
      steps: [
        step("select-project", "Project Name", "Select the approved project."),
        step("view-details", "View Details", "Review approved project details.", [
          "View Details",
        ]),
      ],
    },
    [MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED]: {
      id: "ops-project-assigned",
      title: "Project Assigned",
      shortTitle: "Project Assigned",
      currentAction:
        "Project Name → View Details → Submit the Project",
      pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED],
      steps: [
        step("select-project", "Project Name", "Select the assigned project."),
        step("view-details", "View Details", "Review assigned project details.", [
          "View Details",
        ]),
        step("submit", "Submit the Project", "Submit the project.", [
          "View Details",
          "Submit the Project",
        ]),
      ],
    },
    [MANAGER_BUCKET_KEYS.PROJECT_ONGOING]: {
      id: "ops-project-ongoing",
      title: "Project Ongoing",
      shortTitle: "Project Ongoing",
      currentAction: "Project Name → View Details → Submit Project",
      pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_ONGOING],
      steps: [
        step("select-project", "Select Project", "Select the ongoing project."),
        step("view-details", "View Details", "Review ongoing project progress.", [
          "View Details",
        ]),
        step("submit", "Submit Project", "Submit the project.", [
          "View Details",
          "Submit Project",
        ]),
      ],
    },
    [MANAGER_BUCKET_KEYS.PROJECT_COMPLETED]: {
      id: "ops-project-completed",
      title: "Project Completed",
      shortTitle: "Project Completed",
      currentAction: "Project Name → View Details",
      pathBase: [TOOL_GUIDE_ROOT, MANAGER_BUCKET_KEYS.PROJECT_COMPLETED],
      steps: [
        step("select-project", "Project Name", "Select the completed project."),
        step("view-details", "View Details", "View completed project details.", [
          "View Details",
        ]),
      ],
    },
  },
};



export function getToolGuideForRole(authLevel, bucketName) {
  const roleKey = String(authLevel || "").trim().toUpperCase();
  const roleGuides = toolGuideByRole[roleKey];
  if (!roleGuides) return null;
  return roleGuides[bucketName] || null;
}

export function getCurrentStep(guide, activeStepId) {
  if (!guide?.steps?.length) return null;
  if (!activeStepId) return null;
  return guide.steps.find((s) => s.id === activeStepId) || null;
}

export function getStepIndex(guide, activeStepId) {
  if (!guide?.steps?.length || !activeStepId) return -1;
  return guide.steps.findIndex((s) => s.id === activeStepId);
}

/** Next instructional step, or null when the last step should return to overview. */
export function getNextStep(guide, activeStepId) {
  const steps = guide?.steps || [];
  if (!steps.length) return null;
  const currentIndex = getStepIndex(guide, activeStepId);
  if (currentIndex < 0) return steps[0];
  if (currentIndex >= steps.length - 1) return null;
  return steps[currentIndex + 1];
}

/** Full path segments for a step (pathBase + pathTrail) */
export function buildStepPath(guide, stepId, pathLabel = "") {
  const base = guide?.pathBase?.length
    ? [...guide.pathBase]
    : [TOOL_GUIDE_ROOT];

  if (!stepId && !pathLabel) return base;

  const found = guide?.steps?.find((s) => s.id === stepId);
  if (found?.pathTrail?.length) {
    return [...base, ...found.pathTrail];
  }

  const label = pathLabel || found?.pathLabel || found?.label || "";
  return label ? [...base, label] : base;
}

export function buildPathString(segments = []) {
  return segments.filter(Boolean).join(" / ");
}
